import React, { useState } from 'react';
import type { TriageInsuranceFields, TriageItem } from '../../triage/types';
import { Icon } from '../Icon/Icon';

const FIELDS: { label: string; key: keyof TriageInsuranceFields; mono?: boolean }[] = [
  { label: 'Carrier', key: 'carrier' },
  { label: 'Plan', key: 'plan' },
  { label: 'Member ID', key: 'memberId', mono: true },
  { label: 'Group', key: 'group', mono: true },
  { label: 'Subscriber', key: 'subscriberName' },
  { label: 'Relationship', key: 'relationship' },
  { label: 'Subscriber DOB', key: 'subscriberDob', mono: true },
];

function differsFromTyped(typed: TriageInsuranceFields | undefined, ocr: TriageInsuranceFields): boolean {
  return FIELDS.some(({ key }) => ocr[key] != null && ocr[key] !== typed?.[key]);
}

function FieldGrid({ values }: { values: TriageInsuranceFields }) {
  const shown = FIELDS.filter((f) => values[f.key]);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '14px 20px' }}>
      {shown.map((f) => (
        <div key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span className="v6-eyebrow" style={{ fontSize: 9.5 }}>
            {f.label}
          </span>
          <span
            className={f.mono ? 'v6-mono' : undefined}
            style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink-0)', overflowWrap: 'anywhere' }}
          >
            {values[f.key]}
          </span>
        </div>
      ))}
    </div>
  );
}

export interface TriageInsuranceCardProps {
  item: TriageItem;
  onViewImages?: () => void;
}

export function TriageInsuranceCard({ item, onViewImages }: TriageInsuranceCardProps) {
  const [ocrOpen, setOcrOpen] = useState(false);
  const { insuranceCard, insuranceOcr } = item;
  const hasImages = (item.sourceImages?.length ?? 0) > 0;
  if (!insuranceCard && !insuranceOcr && !hasImages) return null;

  const showOcr = insuranceOcr != null && (!insuranceCard || differsFromTyped(insuranceCard, insuranceOcr));

  return (
    <section className="v6-card v6-fade-up v6-d-2" style={{ marginBottom: 14 }}>
      <header
        style={{
          padding: '14px 20px 12px',
          borderBottom: '1px solid var(--cream-2)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Icon kind="doc" size={14} stroke={2} color="color-mix(in srgb, var(--success) 80%, #000)" />
        <span
          className="v6-display"
          style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-0)', letterSpacing: '-0.01em' }}
        >
          Insurance card
        </span>
        <span style={{ flex: 1 }} />
        {hasImages && onViewImages ? (
          <button
            type="button"
            onClick={onViewImages}
            className="ti-chip-btn"
            style={{ padding: '4px 11px', fontSize: 12, fontWeight: 600 }}
          >
            <Icon kind="external" size={11} stroke={2} />
            {item.sourceImages!.length > 1 ? 'Front & back' : 'View image'}
          </button>
        ) : null}
      </header>

      <div style={{ padding: '16px 20px 18px' }}>
        {insuranceCard ? <FieldGrid values={insuranceCard} /> : null}

        {showOcr && insuranceOcr ? (
          <div style={{ marginTop: insuranceCard ? 16 : 0 }}>
            <button
              type="button"
              onClick={() => setOcrOpen((o) => !o)}
              aria-expanded={ocrOpen}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--ink-2)',
              }}
            >
              <Icon kind={ocrOpen ? 'down' : 'right'} size={10} stroke={2.2} />
              OCR scan
              {insuranceOcr.confidence ? (
                <span className="v6-mono" style={{ fontSize: 11, fontWeight: 500, color: 'var(--ink-3)' }}>
                  {insuranceOcr.confidence}
                </span>
              ) : null}
            </button>
            {ocrOpen ? (
              <div
                style={{
                  marginTop: 12,
                  padding: '14px 16px',
                  background: 'color-mix(in srgb, var(--cream-1) 62%, var(--cream-0))',
                  border: '1px solid var(--cream-2)',
                  borderRadius: 'var(--r-md)',
                }}
              >
                <FieldGrid values={insuranceOcr} />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
